import Phaser from 'phaser';
import { PALETTE } from '../theme';
import { audio } from '../audio/instances';
import { t, localize } from '../i18n';
import { FACTIONS, createFlagElement } from '../turn/factions';
import { ALL_CAMPAIGNS, loadCampaignProgress, isMissionUnlocked } from '../turn/campaign';
import { getCampaignCatalog } from '../turn/scenarioCatalog';
import type { Campaign, CampaignProgress, GameConfig } from '../turn/types';

const ACTIVE_FACTIONS = new Set(['polska']);
const FACTION_CAMPAIGN: Record<string, Campaign> = {
  polska: ALL_CAMPAIGNS[0],
};

export class CampaignScene extends Phaser.Scene {
  private dom: HTMLDivElement | null = null;
  private introOverlay: HTMLDivElement | null = null;
  private selectedFaction: string | null = null;
  private selectedMission = 0; // 0-based index into campaign nodes
  private campaign: Campaign | null = null;
  private progress: CampaignProgress = { completed: {} };

  constructor() {
    super({ key: 'Campaign' });
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.bg);
    this.selectedFaction = null;
    this.selectedMission = 0;
    this.campaign = null;
    this.progress = { completed: {} };
    this.dom = this.buildDom();
    document.body.appendChild(this.dom);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private buildDom(): HTMLDivElement {
    const root = document.createElement('div');
    Object.assign(root.style, {
      position: 'fixed', inset: '0',
      display: 'flex', flexDirection: 'column',
      color: '#d8dee9',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      background: '#0b0d10 url("wallpaper.png") center/cover no-repeat', zIndex: '10',
    } as Partial<CSSStyleDeclaration>);

    const hoverStyle = document.createElement('style');
    hoverStyle.textContent = `.img-btn:hover{filter:brightness(1.2)}.img-btn:active{filter:brightness(0.9);transform:scale(0.97)}`;
    root.appendChild(hoverStyle);

    // Header
    const header = document.createElement('div');
    Object.assign(header.style, {
      display: 'flex', alignItems: 'center', gap: '16px',
      padding: '14px 20px', borderBottom: '1px solid #2a3140',
    });
    const title = document.createElement('span');
    title.textContent = t('campaign.header');
    Object.assign(title.style, { fontSize: '18px', fontWeight: '700', letterSpacing: '2px' });
    header.appendChild(title);
    root.appendChild(header);

    // Body
    const body = document.createElement('div');
    Object.assign(body.style, {
      flex: '1', overflow: 'auto', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    });

    if (!this.selectedFaction) {
      body.appendChild(this.buildFactionGrid());
    } else {
      body.style.alignItems = 'stretch';
      body.style.justifyContent = 'stretch';
      body.appendChild(this.buildMissionView());
    }

    root.appendChild(body);

    // Footer
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      display: 'flex', justifyContent: 'flex-end', gap: '10px',
      padding: '14px 20px', borderTop: '1px solid #2a3140',
    });

    if (this.selectedFaction && this.campaign) {
      const node = this.campaign.nodes[this.selectedMission];
      const unlocked = node ? isMissionUnlocked(node.requires, this.progress) : false;
      const startBtn = document.createElement('button');
      startBtn.className = 'img-btn';
      startBtn.textContent = t('campaign.startMission');
      const imgBtnBase = (bg: string, w: number) => `display:inline-block;width:${w}px;height:46px;border:none;cursor:pointer;background:url('${bg}') no-repeat;background-size:100% 100%;background-color:transparent;color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s`;
      startBtn.style.cssText = imgBtnBase('/graphics/ui/confirmation-button-background.png', 180);
      if (!unlocked) {
        startBtn.style.opacity = '0.5';
        startBtn.style.cursor = 'not-allowed';
        startBtn.style.filter = 'grayscale(0.6)';
      }
      if (unlocked && node) {
        startBtn.addEventListener('click', () => {
          audio.sfx.play('ui-click');
          this.launchMission(node.scenarioId);
        });
      }
      footer.appendChild(startBtn);
    }

    const backBtn = document.createElement('button');
    backBtn.className = 'img-btn';
    backBtn.textContent = t('campaign.back');
    const imgBtnBack = `display:inline-block;width:150px;height:46px;border:none;cursor:pointer;background:url('/graphics/ui/cancel-button-background.png') no-repeat;background-size:100% 100%;background-color:transparent;color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s`;
    backBtn.style.cssText = imgBtnBack;
    backBtn.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      if (this.selectedFaction) {
        this.selectedFaction = null;
        this.rebuildDom();
      } else {
        this.scene.start('GameModeSelect');
      }
    });
    footer.appendChild(backBtn);
    root.appendChild(footer);

    return root;
  }

  private buildFactionGrid(): HTMLDivElement {
    const container = document.createElement('div');
    Object.assign(container.style, {
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px',
    });

    const heading = document.createElement('div');
    heading.textContent = t('campaign.selectFaction');
    Object.assign(heading.style, {
      fontSize: '16px', fontWeight: '600', opacity: '0.7',
      textTransform: 'uppercase', letterSpacing: '2px',
    });
    container.appendChild(heading);

    const grid = document.createElement('div');
    Object.assign(grid.style, {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 160px)',
      gap: '12px',
    });

    for (const faction of FACTIONS) {
      const active = ACTIVE_FACTIONS.has(faction.id);
      const tile = document.createElement('div');
      Object.assign(tile.style, {
        padding: '16px 12px',
        background: active ? '#1a1f2a' : '#111419',
        border: `1px solid ${active ? '#2a3140' : '#1a1f2a'}`,
        borderRadius: '8px',
        textAlign: 'center',
        cursor: active ? 'pointer' : 'not-allowed',
        opacity: active ? '1' : '0.35',
        transition: 'border-color 0.15s, background 0.15s',
      });

      const flag = createFlagElement(faction.id, 80, 54);
      Object.assign(flag.style, { margin: '0 auto 8px', opacity: active ? '1' : '0.5' });
      tile.appendChild(flag);

      const name = document.createElement('div');
      name.textContent = t(`faction.${faction.id}`);
      Object.assign(name.style, {
        fontSize: '13px', fontWeight: '600',
        color: active ? '#d8dee9' : '#666',
      });
      tile.appendChild(name);

      if (!active) {
        const lock = document.createElement('div');
        lock.textContent = t('campaign.locked');
        Object.assign(lock.style, {
          fontSize: '10px', opacity: '0.5', marginTop: '4px',
          fontStyle: 'italic',
        });
        tile.appendChild(lock);
      }

      if (active) {
        tile.addEventListener('mouseenter', () => {
          tile.style.borderColor = '#4a90e2';
          tile.style.background = '#1e2535';
        });
        tile.addEventListener('mouseleave', () => {
          tile.style.borderColor = '#2a3140';
          tile.style.background = '#1a1f2a';
        });
        tile.addEventListener('click', () => {
          audio.sfx.play('ui-click');
          this.selectedFaction = faction.id;
          this.selectedMission = 0;
          this.campaign = FACTION_CAMPAIGN[faction.id] ?? null;
          if (this.campaign) {
            this.progress = loadCampaignProgress(this.campaign.id);
          }
          this.rebuildDom();
          this.maybeShowCampaignIntro(faction.id);
        });
      }

      grid.appendChild(tile);
    }

    container.appendChild(grid);
    return container;
  }

  private buildMissionView(): HTMLDivElement {
    const container = document.createElement('div');
    Object.assign(container.style, {
      display: 'flex', flex: '1', overflow: 'hidden',
    });

    const factionId = this.selectedFaction!;
    const campaign = this.campaign;
    const nodes = campaign?.nodes ?? [];

    // Left: mission list
    const listPanel = document.createElement('div');
    Object.assign(listPanel.style, {
      width: '280px', minWidth: '280px', borderRight: '1px solid #2a3140',
      overflowY: 'auto', padding: '8px',
    });

    const campaignHeader = document.createElement('div');
    campaignHeader.textContent = campaign ? localize(campaign.name) : t(`campaign.${factionId}.name`);
    Object.assign(campaignHeader.style, {
      fontSize: '14px', fontWeight: '700', padding: '8px 14px 4px',
      color: '#d8dee9',
    });
    listPanel.appendChild(campaignHeader);

    const campaignDesc = document.createElement('div');
    campaignDesc.textContent = campaign ? localize(campaign.description) : t(`campaign.${factionId}.desc`);
    Object.assign(campaignDesc.style, {
      fontSize: '11px', padding: '0 14px 10px', opacity: '0.6',
      lineHeight: '1.4', borderBottom: '1px solid #2a3140', marginBottom: '6px',
    });
    listPanel.appendChild(campaignDesc);

    const listLabel = document.createElement('div');
    listLabel.textContent = t('campaign.missions');
    Object.assign(listLabel.style, {
      fontSize: '11px', fontWeight: '600', textTransform: 'uppercase',
      letterSpacing: '1.5px', opacity: '0.5',
      padding: '6px 14px 8px',
    });
    listPanel.appendChild(listLabel);

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const unlocked = isMissionUnlocked(node.requires, this.progress);
      const stars = this.progress.completed[node.scenarioId] ?? 0;

      const item = document.createElement('div');
      Object.assign(item.style, {
        padding: '10px 14px', marginBottom: '2px', borderRadius: '6px',
        cursor: unlocked ? 'pointer' : 'not-allowed',
        border: '1px solid transparent', transition: 'background 0.15s',
        fontSize: '12px', fontWeight: '500',
        opacity: unlocked ? '1' : '0.45',
      });
      const starsStr = stars > 0 ? ` ${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}` : '';
      const lockIcon = unlocked ? '' : '🔒 ';
      item.textContent = `${lockIcon}${i + 1}. ${localize(node.name)}${starsStr}`;

      if (unlocked) {
        item.addEventListener('mouseenter', () => {
          if (i !== this.selectedMission) item.style.background = '#1a1f2a';
        });
        item.addEventListener('mouseleave', () => {
          if (i !== this.selectedMission) item.style.background = 'transparent';
        });
        item.addEventListener('click', () => {
          audio.sfx.play('ui-click');
          this.selectedMission = i;
          this.highlightListItem(listPanel, item);
          this.updateMissionDetail();
        });
      }
      if (i === this.selectedMission) {
        item.style.background = '#253050';
        item.style.borderColor = '#4a90e2';
      }
      listPanel.appendChild(item);
    }
    container.appendChild(listPanel);

    // Right: mission detail
    const rightCol = document.createElement('div');
    Object.assign(rightCol.style, {
      flex: '1', overflow: 'auto', padding: '24px 32px',
    });

    this.thumbnailContainer = document.createElement('div');
    Object.assign(this.thumbnailContainer.style, {
      width: '200px', height: '140px',
      background: '#1a1f2a', border: '1px solid #2a3140', borderRadius: '6px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '48px', color: '#555', userSelect: 'none',
      float: 'left', marginRight: '20px', marginBottom: '12px',
    });
    this.thumbnailContainer.textContent = '\u2694';
    rightCol.appendChild(this.thumbnailContainer);

    this.detailContainer = document.createElement('div');
    rightCol.appendChild(this.detailContainer);
    this.updateMissionDetail();

    container.appendChild(rightCol);
    return container;
  }

  private detailContainer: HTMLDivElement | null = null;
  private thumbnailContainer: HTMLDivElement | null = null;

  private updateMissionDetail(): void {
    if (!this.detailContainer || !this.campaign) return;
    const node = this.campaign.nodes[this.selectedMission];
    if (!node) return;
    const unlocked = isMissionUnlocked(node.requires, this.progress);
    const stars = this.progress.completed[node.scenarioId] ?? 0;
    const starsHtml = stars > 0
      ? `<div style="color:#e0af68;font-size:24px;margin-bottom:8px">${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}</div>`
      : '';
    this.detailContainer.innerHTML = `
      <div style="font-size:22px;font-weight:700;margin-bottom:8px">${localize(node.name)}</div>
      ${starsHtml}
      <div style="font-size:14px;line-height:1.6;opacity:0.85">${localize(node.description)}</div>
      ${!unlocked ? '<div style="color:#f7768e;margin-top:12px;font-size:13px">Misja zablokowana — ukończ poprzednie misje.</div>' : ''}
    `;
  }

  private highlightListItem(listPanel: HTMLElement, active: HTMLElement): void {
    for (const child of listPanel.children) {
      const el = child as HTMLElement;
      el.style.background = 'transparent';
      el.style.borderColor = 'transparent';
    }
    active.style.background = '#253050';
    active.style.borderColor = '#4a90e2';
  }

  private launchMission(scenarioId: string): void {
    const scenario = getCampaignCatalog().byId(scenarioId);
    if (!scenario) return;
    const config: GameConfig = {
      scenario,
      humanPlayer: 0,
      factions: new Map([[0, { factionId: this.campaign?.faction ?? 'polska', characterId: 'a' }]]),
    };
    this.scene.start('TurnGame', { config });
  }

  private rebuildDom(): void {
    this.dom?.remove();
    this.dom = this.buildDom();
    document.body.appendChild(this.dom);
  }

  /** Show the faction's opening narration (e.g. Curie for Poland), if defined.
   *  Plays /audio/voice/campaign-<faction>-intro.mp3 when present — the voice
   *  button is the fallback if autoplay is blocked or no recording exists yet. */
  private maybeShowCampaignIntro(factionId: string): void {
    const text = t(`campaign.${factionId}.intro`);
    if (!text || text === `campaign.${factionId}.intro`) return;
    this.introOverlay?.remove();

    const portrait = FACTIONS.find((f) => f.id === factionId)?.characters[0]?.portrait ?? '';
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', background: 'rgba(5,7,10,0.88)', zIndex: '50',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    } as Partial<CSSStyleDeclaration>);

    const card = document.createElement('div');
    Object.assign(card.style, {
      maxWidth: '760px', maxHeight: '88vh', overflowY: 'auto',
      background: '#14181f', border: '1px solid #2a3140', borderRadius: '10px',
      boxShadow: '0 16px 48px rgba(0,0,0,0.6)', padding: '24px 28px', color: '#e8e2d4',
      display: 'flex', flexDirection: 'column', gap: '16px',
    } as Partial<CSSStyleDeclaration>);

    const head = document.createElement('div');
    Object.assign(head.style, { display: 'flex', alignItems: 'center', gap: '18px' });
    if (portrait) {
      const img = document.createElement('img');
      img.src = portrait;
      Object.assign(img.style, {
        width: '92px', height: '92px', objectFit: 'cover', borderRadius: '8px',
        border: '1px solid #3a4150', flex: '0 0 auto',
      } as Partial<CSSStyleDeclaration>);
      head.appendChild(img);
    }
    const title = document.createElement('div');
    title.textContent = t('campaign.intro.title');
    Object.assign(title.style, { fontSize: '20px', fontWeight: '700', letterSpacing: '1px' });
    head.appendChild(title);
    card.appendChild(head);

    const bodyEl = document.createElement('div');
    Object.assign(bodyEl.style, {
      fontSize: '15px', lineHeight: '1.7', whiteSpace: 'pre-wrap', fontStyle: 'italic', color: '#cdd6e4',
    } as Partial<CSSStyleDeclaration>);
    bodyEl.textContent = text;
    card.appendChild(bodyEl);

    const voice = new Audio(`/audio/voice/campaign-${factionId}-intro.mp3`);
    const buttons = document.createElement('div');
    Object.assign(buttons.style, { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '4px' });
    const btnCss = (bg: string) => `padding:8px 18px;border:1px solid ${bg};background:transparent;color:#e8e2d4;border-radius:6px;cursor:pointer;font-weight:600`;
    const voiceBtn = document.createElement('button');
    voiceBtn.textContent = t('campaign.intro.voice');
    voiceBtn.style.cssText = btnCss('#3a4150');
    voice.onerror = () => { voiceBtn.style.display = 'none'; };
    voiceBtn.addEventListener('click', () => { voice.currentTime = 0; void voice.play().catch(() => {}); });
    const contBtn = document.createElement('button');
    contBtn.textContent = t('campaign.intro.continue');
    contBtn.style.cssText = btnCss('#4a90e2');
    contBtn.addEventListener('click', () => { voice.pause(); overlay.remove(); this.introOverlay = null; });
    buttons.appendChild(voiceBtn);
    buttons.appendChild(contBtn);
    card.appendChild(buttons);

    overlay.appendChild(card);
    document.body.appendChild(overlay);
    this.introOverlay = overlay;
    void voice.play().catch(() => {}); // try autoplay; button is the fallback
  }

  private teardown(): void {
    this.dom?.remove();
    this.dom = null;
    this.introOverlay?.remove();
    this.introOverlay = null;
  }
}
